import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  MapUiContext,
  type MapUiContextType,
  type MvtFilters,
  type MvtLayerSettings,
} from "@/contexts/MapUiContext";
import { DEFAULT_MVT_FILTERS, DEFAULT_MVT_LAYER_SETTINGS } from "@/lib/map-ui-layers-persist";
import type { ReferenceLayerUiState } from "@/types/reference-layer";

const noop = () => {};

/**
 * Минимальный Provider MapUi для встроенной карты L2-модулей
 * (без полного home-state). MapLibreMap использует в основном setActiveTool
 * и канал focusGeometryOnMap через window CustomEvent.
 *
 * `enableReferenceLayers` — локальные тумблеры справочных оверлеев GeoServer
 * (каталог платформы). По умолчанию выкл (visualizer и пр. без регрессии).
 */
export function ModuleMapUiStubProvider({
  children,
  setActiveTool,
  enableReferenceLayers = false,
}: {
  children: ReactNode;
  setActiveTool: (tool: string | null) => void;
  enableReferenceLayers?: boolean;
}) {
  const [referenceLayerUi, setReferenceLayerUi] = useState<
    Record<number, ReferenceLayerUiState>
  >({});

  const setReferenceLayerEnabled = useCallback(
    (layerId: number, enabled: boolean, defaultOpacity = 0.85) => {
      if (!enableReferenceLayers) return;
      setReferenceLayerUi((prev) => {
        const cur = prev[layerId];
        return {
          ...prev,
          [layerId]: {
            enabled,
            opacity: cur?.opacity ?? defaultOpacity,
          },
        };
      });
    },
    [enableReferenceLayers],
  );

  const setReferenceLayerOpacity = useCallback(
    (layerId: number, opacity: number) => {
      if (!enableReferenceLayers) return;
      setReferenceLayerUi((prev) => {
        const cur = prev[layerId];
        return {
          ...prev,
          [layerId]: {
            enabled: cur?.enabled ?? false,
            opacity,
          },
        };
      });
    },
    [enableReferenceLayers],
  );

  const value = useMemo<MapUiContextType>(() => {
    const mvtFilters: MvtFilters = { ...DEFAULT_MVT_FILTERS };
    const mvtLayerSettings: MvtLayerSettings = { ...DEFAULT_MVT_LAYER_SETTINGS };
    return {
      selectedObject: null,
      selectObject: noop,
      clearSelectedObject: noop,
      multiSelectMode: false,
      setMultiSelectMode: noop,
      multiSelectedObjectIds: [],
      toggleMultiSelectedObject: noop,
      setMultiSelectedObjectIds: noop,
      addMultiSelectedObjectIds: noop,
      clearMultiSelection: noop,
      sidebarTab: "objects",
      setSidebarTab: noop,
      isSidebarCollapsed: true,
      toggleSidebar: noop,
      activeTool: null,
      setActiveTool,
      isDrawing: false,
      currentDrawing: null,
      currentObjectId: null,
      selectedGeometry: null,
      setSelectedGeometry: noop,
      focusedGeometryId: null,
      setFocusedGeometryId: noop,
      clearGeometryFocus: noop,
      activeView: "map",
      setActiveView: noop,
      cadastralLayersVisible: false,
      toggleCadastralLayers: noop,
      cadastralOpacity: 0.7,
      setCadastralOpacity: noop,
      telegramIncidentsVisible: false,
      toggleTelegramIncidents: noop,
      telegramOpacity: 0.8,
      setTelegramOpacity: noop,
      workItemsVisible: false,
      toggleWorkItemsVisible: noop,
      workItemsOpacity: 0.8,
      setWorkItemsOpacity: noop,
      showWorkItemCreateButton: false,
      setShowWorkItemCreateButton: noop,
      mapSearchQuery: null,
      setMapSearchQuery: noop,
      showBaseLayerSwitcher: true,
      setShowBaseLayerSwitcher: noop,
      showMapExportButton: false,
      setShowMapExportButton: noop,
      mvtFilters,
      setMvtFilters: noop,
      mvtLayerSettings,
      setMvtLayerSettings: noop,
      referenceLayerUi: enableReferenceLayers ? referenceLayerUi : {},
      setReferenceLayerEnabled: enableReferenceLayers
        ? setReferenceLayerEnabled
        : noop,
      setReferenceLayerOpacity: enableReferenceLayers
        ? setReferenceLayerOpacity
        : noop,
      refreshMapVectorTiles: noop,
      requestEditObject: noop,
      requestEditGeometry: noop,
      requestStartDrawing: noop,
      focusGeometryOnMap: (geometry) => {
        window.dispatchEvent(
          new CustomEvent("focusGeometryOnMap", {
            detail: { geometry, source: "highlight" },
          }),
        );
      },
    };
  }, [
    setActiveTool,
    enableReferenceLayers,
    referenceLayerUi,
    setReferenceLayerEnabled,
    setReferenceLayerOpacity,
  ]);

  return <MapUiContext.Provider value={value}>{children}</MapUiContext.Provider>;
}
